import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { CONTACT_INFO } from '../data/mock';

// Structured policy content — easy to add Terms of Service, Cancellation Policy, etc. later.
const POLICIES = [
  {
    id: 'privacy',
    label: 'Privacy Policy',
    effective: 'September 25, 2026',
    intro: (
      <>
        <p>
          Aurelia (&ldquo;Aurelia,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
          respects your privacy and is committed to protecting the personal information entrusted to
          us. This Privacy Policy explains how we collect, use, disclose, and protect information
          when you visit our website, request or receive our services, communicate with us, or
          otherwise interact with Aurelia.
        </p>
        <p>
          By using our website or services, you acknowledge the practices described in this
          Privacy Policy.
        </p>
      </>
    ),
    sections: [
      {
        title: 'Information we collect',
        body: (
          <>
            <p>Depending on how you interact with Aurelia, we may collect the following categories of information:</p>
            <h4>Personal Information</h4>
            <ul>
              <li>Full name</li>
              <li>Date of birth</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Mailing or service address</li>
              <li>Appointment information</li>
              <li>Identification information when required for a service</li>
              <li>Other information you voluntarily provide to us</li>
            </ul>
            <h4>Health and Laboratory Information</h4>
            <p>
              When necessary to provide our services, we may collect or receive information related to
              laboratory testing, specimen collection, test orders, and other health-related
              information. This may include:
            </p>
            <ul>
              <li>Laboratory test orders</li>
              <li>Specimen collection information</li>
              <li>Test or specimen identifiers</li>
              <li>Information provided by an ordering healthcare provider or laboratory</li>
              <li>Information necessary to coordinate laboratory services</li>
            </ul>
            <p>
              Aurelia does not diagnose medical conditions or interpret laboratory results unless
              expressly authorized and qualified to provide such services.
            </p>
            <h4>Payment Information</h4>
            <p>
              If you purchase services from Aurelia, payment information may be collected and
              processed through third-party payment processors. Aurelia generally does not retain
              complete credit or debit card numbers.
            </p>
            <h4>Website and Technical Information</h4>
            <p>When you visit our website, certain information may automatically be collected, such as:</p>
            <ul>
              <li>IP address</li>
              <li>Browser and device type</li>
              <li>Operating system</li>
              <li>Website pages visited</li>
              <li>Date and time of visits</li>
              <li>Referring website</li>
              <li>General website usage information</li>
            </ul>
            <p>We may use cookies or similar technologies to operate, secure, analyze, and improve our website.</p>
          </>
        ),
      },
      {
        title: 'How we use your information',
        body: (
          <>
            <p>Aurelia may use information we collect to:</p>
            <ul>
              <li>Schedule and manage appointments</li>
              <li>Provide mobile specimen collection services</li>
              <li>Coordinate specimen delivery and laboratory testing</li>
              <li>Communicate with you regarding appointments and services</li>
              <li>Process payments and transactions</li>
              <li>Respond to questions and customer-service requests</li>
              <li>Verify information when necessary to provide services</li>
              <li>Maintain business and service records</li>
              <li>Improve our website, services, and customer experience</li>
              <li>Prevent fraud, misuse, or unauthorized activity</li>
              <li>Meet legal, regulatory, contractual, or professional obligations</li>
              <li>Send promotional or marketing communications when permitted and, where required, with your consent</li>
            </ul>
            <p>We only seek to collect information reasonably necessary for the purposes for which it is being used.</p>
          </>
        ),
      },
      {
        title: 'How we share information',
        body: (
          <>
            <p><strong>Aurelia does not sell your personal information.</strong></p>
            <p>We may disclose information when reasonably necessary to provide our services or operate our business, including to:</p>
            <ul>
              <li>Clinical laboratories and laboratory partners</li>
              <li>Ordering healthcare providers or authorized healthcare organizations</li>
              <li>Service providers that assist with scheduling, payment processing, technology, communications, or business operations</li>
              <li>Professional advisers, such as attorneys, accountants, or insurers</li>
              <li>Government authorities when required by law</li>
              <li>Other parties when you authorize or direct us to disclose information</li>
            </ul>
            <p>
              When Aurelia performs services for a healthcare organization and receives, maintains, or
              transmits protected health information on that organization&rsquo;s behalf, additional
              privacy and security obligations may apply, including obligations under HIPAA and a
              Business Associate Agreement where applicable.
            </p>
          </>
        ),
      },
      {
        title: 'Protection of health information',
        body: (
          <>
            <p>Aurelia takes the privacy of health-related information seriously.</p>
            <p>
              Depending on the services provided and Aurelia&rsquo;s relationship with the applicable
              healthcare provider or laboratory, information may be subject to HIPAA or other
              applicable privacy requirements.
            </p>
            <p>
              Aurelia uses reasonable administrative, technical, and physical safeguards designed to
              protect personal and health-related information against unauthorized access, use,
              disclosure, alteration, or destruction. However, no method of electronic transmission or
              storage can be guaranteed to be completely secure.
            </p>
          </>
        ),
      },
      {
        title: 'Third-party services',
        body: (
          <>
            <p>Our website or services may use third-party companies for functions such as:</p>
            <ul>
              <li>Online scheduling</li>
              <li>Payment processing</li>
              <li>Website hosting</li>
              <li>Email and text communications</li>
              <li>Analytics</li>
              <li>Laboratory testing</li>
              <li>Customer relationship management</li>
              <li>Other business-support services</li>
            </ul>
            <p>These third parties may have access to information necessary to perform services on our behalf or as otherwise permitted by law.</p>
            <p>
              Aurelia does not control the privacy practices of independent third parties. We
              encourage you to review the privacy policies of third-party services before providing
              information through their platforms.
            </p>
          </>
        ),
      },
      {
        title: 'Communications',
        body: (
          <>
            <p>
              By providing your phone number or email address, you may receive communications related
              to appointments, services, billing, account administration, or customer support.
              Where legally permitted, Aurelia may also send promotional communications.
            </p>
            <p>
              You may opt out of promotional email communications by using the unsubscribe
              instructions included in the communication. You may also contact us directly to request
              that promotional communications stop.
            </p>
            <p>Service-related communications may still be sent when necessary to administer an appointment or provide requested services.</p>
          </>
        ),
      },
      {
        title: 'Cookies and website technologies',
        body: (
          <>
            <p>Our website may use cookies, pixels, analytics tools, or similar technologies to help us:</p>
            <ul>
              <li>Keep the website functioning properly</li>
              <li>Understand website traffic and usage</li>
              <li>Improve website performance</li>
              <li>Remember certain preferences</li>
              <li>Detect security issues</li>
              <li>Measure marketing effectiveness</li>
            </ul>
            <p>You may be able to control cookies through your browser settings. Disabling certain cookies may affect website functionality.</p>
          </>
        ),
      },
      {
        title: 'Data retention',
        body: (
          <>
            <p>
              Aurelia retains information for as long as reasonably necessary to provide services,
              maintain appropriate business and laboratory records, comply with contractual and legal
              obligations, resolve disputes, enforce agreements, and protect our legitimate business
              interests. Retention periods may vary depending on the type of information and the
              service involved.
            </p>
            <p>
              Where Aurelia is subject to specific contractual, laboratory, healthcare, or legal
              record-retention requirements, those requirements may determine how long certain
              information is retained.
            </p>
          </>
        ),
      },
      {
        title: 'Your privacy rights',
        body: (
          <>
            <p>
              Depending on applicable law and the circumstances in which Aurelia maintains your
              information, you may have certain rights regarding your personal information.
            </p>
            <p>
              These may include the ability to request information about how your personal information
              is used or to request access, correction, or other appropriate action concerning your
              information.
            </p>
            <p>
              Certain health information may be subject to separate privacy requirements and
              procedures depending on whether Aurelia or another healthcare organization maintains the
              information.
            </p>
            <p>To submit a privacy-related request, please contact us using the information provided below.</p>
          </>
        ),
      },
      {
        title: "Children's privacy",
        body: (
          <>
            <p>
              Our website and services are not intended to knowingly collect personal information from
              children through the website without appropriate authorization.
            </p>
            <p>
              Because Aurelia may provide specimen collection services to individuals of various ages,
              including minors when appropriately authorized, information collected in connection with
              an actual healthcare or laboratory service may be handled according to applicable laws,
              authorization requirements, and contractual obligations.
            </p>
          </>
        ),
      },
      {
        title: 'Data security',
        body: (
          <>
            <p>Aurelia maintains reasonable safeguards designed to protect information from unauthorized access, disclosure, alteration, or destruction.</p>
            <p>
              These safeguards may include appropriate access controls, secure systems, employee or
              contractor procedures, and other administrative, technical, and physical protections
              appropriate to the information we maintain.
            </p>
            <p>No website, electronic transmission, or storage system is completely secure, and Aurelia cannot guarantee absolute security.</p>
          </>
        ),
      },
      {
        title: 'Data breaches',
        body: (
          <>
            <p>
              If Aurelia experiences a security incident involving personal or health information, we
              will take reasonable steps to investigate, contain, and respond to the incident and
              provide notifications when required by applicable law or contractual obligations.
            </p>
            <p>
              Depending on the type of information and Aurelia&rsquo;s role, different federal or
              state breach-notification requirements may apply. Certain health-information breaches
              may also be subject to federal requirements.
            </p>
          </>
        ),
      },
      {
        title: 'Links to other websites',
        body: (
          <>
            <p>Our website may contain links to third-party websites or services.</p>
            <p>Aurelia is not responsible for the privacy, security, or content practices of third-party websites. We encourage you to review the privacy policies of websites you visit through external links.</p>
          </>
        ),
      },
      {
        title: 'Changes to this Privacy Policy',
        body: (
          <>
            <p>Aurelia may update this Privacy Policy periodically to reflect changes in our services, technology, business practices, or applicable legal requirements.</p>
            <p>
              When we make changes, we will update the &ldquo;Effective Date&rdquo; at the top of this
              policy. Your continued use of our website after an updated Privacy Policy is posted
              indicates acceptance of the revised policy to the extent permitted by law.
            </p>
          </>
        ),
      },
      {
        title: 'Contact us',
        body: (
          <>
            <p>If you have questions about this Privacy Policy or would like to submit a privacy-related request, please contact:</p>
            <div className="bg-[#faf6f0] border-l-2 border-[#c9a961] p-5 mt-4 space-y-1 not-prose">
              <p className="text-[13px] tracking-[0.22em] text-[#8a7132] font-semibold">AURELIA</p>
              <p className="text-[#3c3c3c] text-[14px]">Atlanta, Georgia</p>
              <p className="text-[#3c3c3c] text-[14px]">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.email}</a></p>
              <p className="text-[#3c3c3c] text-[14px]">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9+]/g, '')}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.phone}</a></p>
              <p className="text-[#3c3c3c] text-[14px]">Website: aureliawellness.com</p>
            </div>
          </>
        ),
      },
    ],
    footnote: (
      <>
        <strong>Important:</strong> This Privacy Policy is intended to provide general information
        about Aurelia&rsquo;s privacy practices and should be reviewed by a qualified attorney before
        publication, particularly because Aurelia may handle health and laboratory information.
        Privacy obligations can vary depending on whether Aurelia is acting directly for consumers,
        an ordering provider, a laboratory, or another healthcare organization. HIPAA
        business-associate obligations, when applicable, generally require written arrangements
        addressing the handling and safeguarding of protected health information.
      </>
    ),
  },
  {
    id: 'terms',
    label: 'Terms & Conditions',
    effective: 'September 25, 2026',
    intro: (
      <>
        <p>
          Welcome to Aurelia (&ldquo;Aurelia,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
          These Terms &amp; Conditions govern your use of the Aurelia website and your purchase or use
          of Aurelia&rsquo;s mobile specimen collection and related services.
        </p>
        <p>
          By accessing our website, booking an appointment, or purchasing or using our services, you
          agree to these Terms &amp; Conditions. If you do not agree, please do not use our website
          or services.
        </p>
      </>
    ),
    sections: [
      {
        title: 'Our services',
        body: (
          <>
            <p>Aurelia provides mobile specimen collection and related concierge laboratory services. Depending on the services offered, this may include:</p>
            <ul>
              <li>Mobile blood specimen collection</li>
              <li>Urine specimen collection</li>
              <li>DNA specimen collection</li>
              <li>Drug and alcohol specimen collection</li>
              <li>DOT-related specimen collection, where applicable</li>
              <li>Wellness and laboratory specimen collection</li>
              <li>Corporate and executive wellness collection services</li>
              <li>Other specimen-collection services offered by Aurelia</li>
            </ul>
            <p>Aurelia primarily collects and handles specimens for submission to an appropriate laboratory or testing organization.</p>
            <p>Aurelia is not a laboratory and does not independently perform laboratory testing unless specifically stated otherwise.</p>
            <p>Laboratory testing, analysis, interpretation, and reporting are performed by the applicable laboratory or testing organization.</p>
          </>
        ),
      },
      {
        title: 'No medical diagnosis or treatment',
        body: (
          <>
            <p>Aurelia&rsquo;s specimen collection services do not constitute medical diagnosis, medical treatment, or medical advice.</p>
            <p>Aurelia does not diagnose disease, prescribe medication, determine treatment plans, or independently interpret laboratory results as a healthcare provider.</p>
            <p>Questions regarding the medical meaning of laboratory results should be directed to your physician, qualified healthcare provider, or the appropriate laboratory.</p>
            <p><strong>If you are experiencing a medical emergency, call 911 or seek immediate medical attention.</strong></p>
          </>
        ),
      },
      {
        title: 'Laboratory test orders',
        body: (
          <>
            <p>Certain laboratory tests may require an order or authorization from a qualified healthcare provider.</p>
            <p>When an order is required, the customer is responsible for obtaining a valid order from an appropriate healthcare provider unless Aurelia specifically offers a legally permissible alternative.</p>
            <p>Aurelia may decline or postpone collection when:</p>
            <ul>
              <li>A required order is missing, incomplete, expired, or invalid</li>
              <li>The requested test is unavailable</li>
              <li>Required patient information is missing</li>
              <li>The laboratory cannot accept the requested specimen</li>
              <li>Collection requirements cannot be safely or properly met</li>
              <li>Applicable law, laboratory policy, or professional requirements prevent collection</li>
            </ul>
          </>
        ),
      },
      {
        title: 'Appointments',
        body: (
          <>
            <p>Appointments may be scheduled through Aurelia&rsquo;s website, telephone, email, or other authorized booking methods.</p>
            <p>Customers are responsible for providing accurate information when scheduling an appointment, including:</p>
            <ul>
              <li>Name</li>
              <li>Contact information</li>
              <li>Service address</li>
              <li>Requested service</li>
              <li>Test or collection information</li>
              <li>Any other information reasonably necessary to complete the appointment</li>
            </ul>
            <p>An appointment is not considered confirmed until Aurelia provides confirmation through its designated scheduling system or another authorized communication method.</p>
          </>
        ),
      },
      {
        title: 'Mobile service',
        body: (
          <>
            <p>Because Aurelia provides mobile services, a valid and reasonably accessible service location must be provided.</p>
            <p>Customers are responsible for ensuring that Aurelia&rsquo;s collection professional can safely access the agreed-upon location.</p>
            <p>Aurelia may refuse or discontinue a collection when conditions create an unreasonable safety concern, including but not limited to:</p>
            <ul>
              <li>Threatening or violent behavior</li>
              <li>Unsafe premises</li>
              <li>Exposure to hazardous materials</li>
              <li>Uncontrolled animals</li>
              <li>Illegal activity occurring at the location</li>
              <li>Lack of appropriate access to the patient</li>
              <li>Conditions that prevent safe specimen collection</li>
            </ul>
            <p>If a collection cannot safely be completed, applicable cancellation or service fees may apply.</p>
          </>
        ),
      },
      {
        title: 'Patient preparation',
        body: (
          <>
            <p>Customers are responsible for following any preparation instructions provided by their healthcare provider, laboratory, or Aurelia before their appointment.</p>
            <p>Certain laboratory tests may require fasting, medication restrictions, hydration requirements, specific collection times, or other preparation.</p>
            <p>Aurelia does not determine medical preparation requirements unless specifically authorized to provide such instructions.</p>
            <p>Failure to properly prepare may affect whether a specimen can be collected or whether the laboratory can perform the requested testing.</p>
          </>
        ),
      },
      {
        title: 'Specimen collection',
        body: (
          <>
            <p>Aurelia&rsquo;s trained personnel will use reasonable professional procedures for specimen collection.</p>
            <p>Although Aurelia takes reasonable precautions to minimize risks, specimen collection may involve ordinary risks such as:</p>
            <ul>
              <li>Temporary pain or discomfort</li>
              <li>Bruising</li>
              <li>Minor bleeding</li>
              <li>Dizziness or lightheadedness</li>
              <li>Fainting</li>
              <li>Minor irritation</li>
              <li>Rare complications associated with specimen collection</li>
            </ul>
            <p>Customers should inform the collection professional of relevant circumstances that may affect collection, including previous fainting, difficulty with blood draws, or other relevant concerns.</p>
          </>
        ),
      },
      {
        title: 'Specimen handling and transport',
        body: (
          <>
            <p>Aurelia will handle collected specimens according to applicable procedures and requirements.</p>
            <p>Specimens may be transported or transferred to an appropriate laboratory, testing facility, courier, healthcare provider, or other authorized recipient.</p>
            <p>Once a specimen has been transferred to an independent laboratory or other authorized testing organization, Aurelia is not responsible for the laboratory&rsquo;s testing process, turnaround time, interpretation, reporting, or internal procedures.</p>
          </>
        ),
      },
      {
        title: 'Laboratory results',
        body: (
          <>
            <p>Laboratory results are generated by the applicable testing laboratory.</p>
            <p>Turnaround times are estimates and may vary because of laboratory workload, specimen quality, transportation, testing requirements, weekends, holidays, equipment issues, or other circumstances outside Aurelia&rsquo;s control.</p>
            <p>Aurelia does not guarantee a particular testing turnaround time or result.</p>
            <p>Customers should review laboratory results with a qualified healthcare provider when medical interpretation is necessary.</p>
          </>
        ),
      },
      {
        title: 'Payments',
        body: (
          <>
            <p>
              All payments to Aurelia are processed securely through our <strong>Square point-of-sale
              system</strong>. Payment is collected <strong>before services are rendered</strong>. For
              customers who prefer to pay over time, Aurelia also accepts <strong>Afterpay</strong>{' '}
              (buy-now-pay-later) at the point of sale, subject to Afterpay&rsquo;s approval and terms.
            </p>
            <p>Prices may vary depending on:</p>
            <ul>
              <li>Type of collection</li>
              <li>Number or type of specimens</li>
              <li>Mobile travel requirements</li>
              <li>Laboratory testing fees</li>
              <li>After-hours or expedited services</li>
              <li>Corporate or group services</li>
              <li>Other applicable service charges</li>
            </ul>
            <p>Aurelia will communicate applicable charges before or during the booking process when reasonably possible.</p>
            <p>Unless expressly stated otherwise, payment for Aurelia&rsquo;s collection service is separate from fees charged by an independent laboratory or healthcare provider.</p>
          </>
        ),
      },
      {
        title: 'Laboratory fees',
        body: (
          <>
            <p>Some services may involve laboratory testing fees that are separate from Aurelia&rsquo;s mobile collection fee.</p>
            <p>Where applicable, customers may be responsible for laboratory charges, testing fees, physician-order fees, or other third-party charges.</p>
            <p>Aurelia does not control pricing established by independent laboratories or healthcare providers.</p>
          </>
        ),
      },
      {
        title: 'Cancellations and rescheduling',
        body: (
          <>
            <p>Aurelia understands that plans can change. Customers should provide as much notice as possible when canceling or rescheduling an appointment.</p>
            <p><strong>Cancellations incur a $40 flat fee. Same-day cancellations are not refunded.</strong> Full details are available in Aurelia&rsquo;s <a href="/policies#cancellation" className="text-[#8a7132] underline hover:text-[#c9a961] font-medium">Cancellation Policy</a>.</p>
            <p>Aurelia reserves the right to charge applicable fees when a customer is unavailable at the scheduled appointment time or when a collection cannot be completed because of circumstances within the customer&rsquo;s control.</p>
          </>
        ),
      },
      {
        title: 'Refunds',
        body: (
          <>
            <p>Refund eligibility depends on the service purchased and whether the service has already been performed.</p>
            <p>Once a specimen collection has been completed, fees for the completed collection service generally are not refundable solely because a customer later changes their mind about testing.</p>
            <p>Laboratory testing fees paid to an independent laboratory may be subject to that laboratory&rsquo;s own refund policies.</p>
            <p>Additional refund terms may be provided in Aurelia&rsquo;s separate Cancellation &amp; Refund Policy.</p>
          </>
        ),
      },
      {
        title: 'DNA, drug, alcohol, and other specialty collections',
        body: (
          <>
            <p>Certain services may have additional collection, identification, documentation, chain-of-custody, consent, or laboratory requirements.</p>
            <p>For DNA, drug, alcohol, DOT, legal, employment, or other regulated testing, customers agree to provide accurate information and follow all applicable collection and identification procedures.</p>
            <p>Aurelia does not guarantee that a specimen will satisfy the requirements of a particular court, employer, government agency, laboratory, or other organization unless Aurelia expressly agrees to those requirements.</p>
          </>
        ),
      },
      {
        title: 'Corporate and group services',
        body: (
          <>
            <p>Corporate, executive, wellness-day, and group appointments may be governed by additional written agreements, proposals, invoices, or service contracts.</p>
            <p>If a separate written agreement conflicts with these Terms &amp; Conditions, the specific written agreement will control for that particular service.</p>
          </>
        ),
      },
      {
        title: 'Third-party laboratories and service providers',
        body: (
          <>
            <p>Aurelia may work with independent laboratories, courier services, payment processors, scheduling platforms, technology providers, and other third parties.</p>
            <p>Aurelia is not responsible for the acts, omissions, systems, policies, testing processes, delays, or decisions of independent third parties except to the extent required by applicable law.</p>
          </>
        ),
      },
      {
        title: 'Website use',
        body: (
          <>
            <p>You agree to use the Aurelia website only for lawful purposes.</p>
            <p>You may not:</p>
            <ul>
              <li>Attempt to gain unauthorized access to the website or its systems</li>
              <li>Interfere with website functionality</li>
              <li>Submit fraudulent or misleading information</li>
              <li>Use the website for unlawful purposes</li>
              <li>Copy, reproduce, modify, or commercially exploit website content without permission</li>
              <li>Introduce malicious software or other harmful technology</li>
            </ul>
            <p>Aurelia reserves the right to restrict or terminate access to the website when reasonably necessary to protect the website, business, customers, or third parties.</p>
          </>
        ),
      },
      {
        title: 'Intellectual property',
        body: (
          <>
            <p>Unless otherwise stated, the Aurelia name, logo, branding, website content, graphics, photographs, written materials, and other original content are owned by or licensed to Aurelia.</p>
            <p>You may not reproduce, distribute, modify, sell, or commercially use Aurelia&rsquo;s intellectual property without prior written permission.</p>
          </>
        ),
      },
      {
        title: 'Privacy',
        body: (
          <>
            <p>Your use of Aurelia&rsquo;s website and services is also subject to Aurelia&rsquo;s Privacy Policy.</p>
            <p>Our Privacy Policy explains how we collect, use, disclose, and protect information.</p>
          </>
        ),
      },
      {
        title: 'Disclaimer of warranties',
        body: (
          <>
            <p>To the extent permitted by law, Aurelia provides its website and services on an &ldquo;as available&rdquo; basis.</p>
            <p>Aurelia does not guarantee that:</p>
            <ul>
              <li>The website will always be available or error-free</li>
              <li>A requested laboratory test will always be available</li>
              <li>A laboratory will accept every specimen</li>
              <li>A laboratory result will be available within a particular timeframe</li>
              <li>A specimen will produce a particular result</li>
              <li>Third-party systems will operate without interruption</li>
            </ul>
            <p>Nothing in these Terms &amp; Conditions is intended to exclude rights or protections that cannot legally be excluded.</p>
          </>
        ),
      },
      {
        title: 'Limitation of liability',
        body: (
          <>
            <p>To the maximum extent permitted by applicable law, Aurelia will not be responsible for indirect, incidental, special, consequential, or punitive damages arising from use of the website or services.</p>
            <p>Nothing in these Terms &amp; Conditions limits liability that cannot legally be limited or excluded under applicable law.</p>
          </>
        ),
      },
      {
        title: 'Indemnification',
        body: (
          <p>To the extent permitted by law, you agree to hold Aurelia and its owners, employees, contractors, and authorized representatives harmless from claims, losses, damages, liabilities, and reasonable expenses arising from your unlawful use of the website, intentional misconduct, fraudulent information, or violation of these Terms &amp; Conditions.</p>
        ),
      },
      {
        title: 'Governing law',
        body: (
          <>
            <p>These Terms &amp; Conditions shall be governed by the laws of the State of Georgia, without regard to conflict-of-law principles, except where federal law or another applicable law requires otherwise.</p>
            <p>Any dispute will be handled in a court or other legally appropriate forum with proper jurisdiction.</p>
          </>
        ),
      },
      {
        title: 'Changes to these Terms',
        body: (
          <>
            <p>Aurelia may modify these Terms &amp; Conditions from time to time.</p>
            <p>Updated Terms &amp; Conditions will be posted on this website with a revised Effective Date.</p>
            <p>Your continued use of the website or services after updated terms are posted constitutes acceptance of the revised terms to the extent permitted by law.</p>
          </>
        ),
      },
      {
        title: 'Severability',
        body: (
          <p>If any provision of these Terms &amp; Conditions is determined to be invalid or unenforceable, the remaining provisions will remain in effect to the fullest extent permitted by law.</p>
        ),
      },
      {
        title: 'Entire agreement',
        body: (
          <p>These Terms &amp; Conditions, together with any applicable service agreements, policies, consents, and other written agreements provided by Aurelia, constitute the applicable agreement between you and Aurelia regarding your use of the website and services.</p>
        ),
      },
      {
        title: 'Contact Aurelia',
        body: (
          <>
            <p>Questions regarding these Terms &amp; Conditions may be directed to:</p>
            <div className="bg-[#faf6f0] border-l-2 border-[#c9a961] p-5 mt-4 space-y-1 not-prose">
              <p className="text-[13px] tracking-[0.22em] text-[#8a7132] font-semibold">AURELIA</p>
              <p className="text-[#3c3c3c] text-[14px]">Atlanta, Georgia</p>
              <p className="text-[#3c3c3c] text-[14px]">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.email}</a></p>
              <p className="text-[#3c3c3c] text-[14px]">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9+]/g, '')}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.phone}</a></p>
              <p className="text-[#3c3c3c] text-[14px]">Website: aureliawellness.com</p>
            </div>
          </>
        ),
      },
    ],
    footnote: (
      <>
        <strong>Important:</strong> These Terms &amp; Conditions are a general business template and
        should be reviewed by a Georgia attorney before publication, particularly because Aurelia may
        provide services involving health information, laboratory testing, DOT collections, DNA
        testing, and other regulated services.
      </>
    ),
  },
  {
    id: 'cancellation',
    label: 'Cancellation Policy',
    effective: 'September 25, 2026',
    intro: (
      <>
        <p>
          Aurelia is a mobile concierge service and each appointment is reserved specifically for
          you. When an appointment is cancelled we lose the ability to serve another client in that
          window &mdash; the fees below help keep our schedule fair for everyone.
        </p>
        <div className="bg-[#141618] text-white p-6 mt-4 not-prose">
          <p className="text-[10px] tracking-[0.3em] text-[#c9a961] font-semibold mb-2">POLICY AT A GLANCE</p>
          <p className="text-[16px] leading-relaxed" data-testid="cancellation-headline">
            <strong className="text-[#c9a961]">Cancellations incur a $40 flat fee.</strong>{' '}
            <strong className="text-[#c9a961]">Same-day cancellations are not refunded.</strong>
          </p>
        </div>
      </>
    ),
    sections: [
      {
        title: 'Cancellation fee',
        body: (
          <p>
            A flat <strong>$40 cancellation fee</strong> applies to any appointment cancelled before
            the day of service. The fee is deducted from any deposit or prepayment on file, or
            invoiced separately if no deposit was collected.
          </p>
        ),
      },
      {
        title: 'Same-day cancellations',
        body: (
          <>
            <p>
              <strong>Same-day cancellations are not refunded.</strong> If an appointment is
              cancelled on the scheduled day of service &mdash; for any reason within the client&rsquo;s
              control &mdash; the full service fee is retained.
            </p>
            <p>
              Because our specialist has already blocked travel time and, in many cases, sourced
              specimen supplies, same-day slots cannot be re-booked.
            </p>
          </>
        ),
      },
      {
        title: 'No-shows and inaccessible locations',
        body: (
          <>
            <p>
              A no-show &mdash; or an inability to complete the collection because the specialist
              cannot safely access the client at the confirmed location &mdash; is treated as a
              same-day cancellation. The full service fee is retained.
            </p>
            <p>
              Common examples include: no one available at the door, unsafe premises, uncontrolled
              animals, or the client not being reasonably prepared for the appointment.
            </p>
          </>
        ),
      },
      {
        title: 'Rescheduling',
        body: (
          <>
            <p>
              Rescheduling with <strong>at least 24 hours&rsquo; notice</strong> is complimentary. Use
              the link in your confirmation email or contact us directly.
            </p>
            <p>
              Rescheduling requests made within 24 hours of the appointment may be treated as a
              cancellation and subject to the fees above.
            </p>
          </>
        ),
      },
      {
        title: 'Aurelia-initiated changes',
        body: (
          <p>
            In the rare event Aurelia needs to cancel or reschedule (illness, weather, safety),
            no fees apply and any deposit is refunded in full or applied to the next available
            appointment at your preference.
          </p>
        ),
      },
      {
        title: 'Third-party laboratory fees',
        body: (
          <p>
            Laboratory testing fees paid directly to an independent laboratory are governed by that
            laboratory&rsquo;s own refund policy and are outside the scope of Aurelia&rsquo;s
            cancellation policy.
          </p>
        ),
      },
      {
        title: 'How to cancel or reschedule',
        body: (
          <>
            <p>Use the personal link in your appointment confirmation email or reach us directly:</p>
            <div className="bg-[#faf6f0] border-l-2 border-[#c9a961] p-5 mt-4 space-y-1 not-prose">
              <p className="text-[13px] tracking-[0.22em] text-[#8a7132] font-semibold">AURELIA</p>
              <p className="text-[#3c3c3c] text-[14px]">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.email}</a></p>
              <p className="text-[#3c3c3c] text-[14px]">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9+]/g, '')}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.phone}</a></p>
            </div>
          </>
        ),
      },
    ],
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    effective: 'September 25, 2026',
    intro: (
      <>
        <p>
          Aurelia is committed to making our website, services, and digital experiences accessible
          and welcoming to as many people as possible, including individuals with disabilities.
        </p>
        <p>
          We strive to provide an inclusive experience for all customers and to make reasonable
          efforts to improve the accessibility and usability of our website.
        </p>
      </>
    ),
    sections: [
      {
        title: 'Website accessibility',
        body: (
          <>
            <p>
              Aurelia is working to improve the accessibility of our website and aims to follow
              recognized accessibility standards and best practices, including the Web Content
              Accessibility Guidelines (WCAG).
            </p>
            <p>
              We strive to make our website accessible through a variety of devices and assistive
              technologies, including screen readers, keyboard navigation, and other accessibility
              tools.
            </p>
            <p>
              Because accessibility is an ongoing process, some portions of our website may not yet
              be fully accessible. We continuously work to identify and address accessibility
              barriers.
            </p>
          </>
        ),
      },
      {
        title: 'Accessibility support',
        body: (
          <>
            <p>
              If you experience difficulty accessing any portion of our website, booking an
              appointment, understanding our online information, or accessing our services because
              of a disability, please contact us.
            </p>
            <p>
              We will make reasonable efforts to provide the information or assistance you need
              through an accessible alternative.
            </p>
            <p>Please contact us with:</p>
            <ul>
              <li>The webpage or service you are having difficulty accessing</li>
              <li>A description of the accessibility issue, if possible</li>
              <li>Your preferred method of communication</li>
              <li>Any reasonable accommodation or alternative format you are requesting</li>
            </ul>
          </>
        ),
      },
      {
        title: 'Mobile service accessibility',
        body: (
          <>
            <p>
              Aurelia provides mobile specimen collection services and strives to accommodate
              customers with disabilities whenever reasonably possible.
            </p>
            <p>
              If you require a reasonable accommodation for a mobile appointment, please notify us
              when scheduling your appointment or contact us as soon as possible.
            </p>
            <p>
              Accessibility needs may include reasonable accommodations related to communication,
              physical access, appointment procedures, or other aspects of receiving our services.
            </p>
            <p>
              Certain accommodations may depend on the physical characteristics of the service
              location and applicable safety requirements.
            </p>
          </>
        ),
      },
      {
        title: 'Contact us',
        body: (
          <>
            <p>For accessibility questions, accommodation requests, or to report an accessibility concern, please contact:</p>
            <div className="bg-[#faf6f0] border-l-2 border-[#c9a961] p-5 mt-4 space-y-1 not-prose">
              <p className="text-[13px] tracking-[0.22em] text-[#8a7132] font-semibold">AURELIA</p>
              <p className="text-[#3c3c3c] text-[14px]">Atlanta, Georgia</p>
              <p className="text-[#3c3c3c] text-[14px]">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.email}</a></p>
              <p className="text-[#3c3c3c] text-[14px]">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9+]/g, '')}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.phone}</a></p>
            </div>
            <p className="mt-4">We welcome accessibility feedback and will make reasonable efforts to respond to accessibility concerns promptly.</p>
          </>
        ),
      },
      {
        title: 'Ongoing commitment',
        body: (
          <>
            <p>
              Accessibility is an ongoing commitment at Aurelia. We will continue evaluating our
              website and customer experience and making reasonable improvements to promote
              accessibility and inclusion.
            </p>
            <p className="text-[13px] text-[#6b6b6b] pt-2">Last updated: September 25, 2026</p>
          </>
        ),
      },
    ],
  },
];

// Individual policy renderer with collapsible sections for long reads.
const PolicySection = ({ index, title, body }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-t border-black/10" data-testid={`policy-section-${index}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 text-left"
        data-testid={`policy-section-toggle-${index}`}
      >
        <div className="flex items-center gap-4">
          <span className="text-[11px] tracking-[0.28em] text-[#c9a961] font-semibold w-10">
            {String(index + 1).padStart(2, '0')}
          </span>
          <h3
            className="font-serif text-[22px] md:text-[26px] text-[#1c1e20] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {title}
          </h3>
        </div>
        {open ? (
          <ChevronUp size={18} className="text-[#c9a961] flex-shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-[#c9a961] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div
          className="pl-0 md:pl-14 pb-8 text-[#3c3c3c] leading-[1.85] text-[15px] space-y-4 policy-prose"
          data-testid={`policy-section-body-${index}`}
        >
          {body}
        </div>
      )}
    </div>
  );
};

const Policies = () => {
  const { hash } = useLocation();
  const [activeId, setActiveId] = useState(POLICIES[0].id);

  // Deep-link via hash: /policies#accessibility → open the Accessibility pill.
  useEffect(() => {
    const wanted = (hash || '').replace('#', '');
    if (wanted && POLICIES.some((p) => p.id === wanted)) {
      setActiveId(wanted);
    }
  }, [hash]);

  const active = POLICIES.find((p) => p.id === activeId) || POLICIES[0];

  return (
    <div className="min-h-[calc(100vh-160px)] bg-[#faf6f0] py-14 md:py-20 px-6" data-testid="policies-page">
      <style>{`
        .policy-prose h4 { font-family: 'Cormorant Garamond', serif; font-weight: 500; font-size: 20px; color: #1c1e20; margin-top: 8px; margin-bottom: 4px; }
        .policy-prose ul { list-style: disc; padding-left: 22px; }
        .policy-prose ul li { margin-bottom: 4px; }
      `}</style>
      <div className="max-w-[900px] mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-full bg-white border border-[#c9a961] flex items-center justify-center mx-auto mb-5">
            <ShieldCheck size={22} className="text-[#c9a961]" strokeWidth={1.6} />
          </div>
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-3">POLICIES</p>
          <h1
            className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#1c1e20]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {active.label}
          </h1>
          <div className="w-16 h-[2px] bg-[#c9a961] mx-auto my-5" />
          <p className="text-[12px] text-[#6b6b6b] tracking-wide" data-testid="policy-effective-date">
            EFFECTIVE DATE: {active.effective.toUpperCase()}
          </p>
        </div>

        {/* Policy switcher — pill-style, holds room for future policies */}
        {POLICIES.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {POLICIES.map((p) => (
              <button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                data-testid={`policy-tab-${p.id}`}
                className={`px-5 py-2.5 text-[11px] tracking-[0.24em] font-semibold border transition-colors ${
                  activeId === p.id
                    ? 'bg-[#1c1e20] border-[#1c1e20] text-white'
                    : 'bg-white border-black/15 text-[#3c3c3c] hover:border-[#c9a961]'
                }`}
              >
                {p.label.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <article className="bg-white border border-black/5 p-8 md:p-12 shadow-sm">
          <div className="text-[#3c3c3c] leading-[1.85] text-[15px] space-y-4 mb-4" data-testid="policy-intro">
            {active.intro}
          </div>

          <div className="mt-6">
            {active.sections.map((s, i) => (
              <PolicySection key={s.title} index={i} title={s.title} body={s.body} />
            ))}
          </div>

          {active.footnote && (
            <p className="text-[13px] text-[#6b6b6b] leading-relaxed mt-10 pt-6 border-t border-black/10 italic">
              {active.footnote}
            </p>
          )}
        </article>
      </div>
    </div>
  );
};

export default Policies;
